import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { deriveVariantPath, mediaLibraryProxyUrl } from "@/lib/media-library";
import {
  SITE_IMAGE_SLOTS,
  widthsForSlot,
  type SiteImageSlotKey,
  type SiteImageSlotsResolved,
} from "@/lib/site-image-slots";
import {
  deleteSharedEdgeCache,
  readSharedEdgeCache,
  writeSharedEdgeCache,
} from "@/server/site/edge-cache";

// Ojo: la fn admin (adminAssignSiteImageSlot) vive en
// site-image-slots-admin.fns.ts, NO acá — este archivo se importa desde el
// __root del sitio público, y su validator de zod arrastraba zod al JS
// inicial de todas las páginas públicas.

function emptyResolved(): SiteImageSlotsResolved {
  const resolved = {} as SiteImageSlotsResolved;
  for (const slot of SITE_IMAGE_SLOTS) resolved[slot.key] = null;
  return resolved;
}

// Cache en memoria a nivel de módulo (mismo patrón que `_tokenCache` de
// require-admin-auth.ts): los slots casi nunca cambian, así que evitar una
// query en cada request de la home/páginas de servicio importa más que la
// propagación instantánea — hasta 60s de demora es aceptable frente a la
// alternativa actual (un deploy completo). Se suma un nivel L2 de Cache API
// de Cloudflare (edge-cache.ts) compartido entre isolates del mismo
// datacenter, para que un isolate reciclado no pague el round-trip completo
// a Supabase (ver el hallazgo de TTFB de campo lento que motiva esto).
let slotsCache: { data: SiteImageSlotsResolved; expiresAt: number } | null = null;
const SLOTS_CACHE_TTL = 60_000;
const SLOTS_EDGE_CACHE_KEY = "public-site-image-slots";

export async function invalidateSlotsCache() {
  slotsCache = null;
  await deleteSharedEdgeCache(SLOTS_EDGE_CACHE_KEY);
}

/**
 * Lectura pública (sin autenticación) de los slots de imagen del sitio, para
 * el loader raíz (SSR de home + páginas de servicio). Degrada a "todo sin
 * asignar" ante cualquier error: el sitio público nunca debe romperse por
 * esto (mismo criterio que getPublicMarketingConfig).
 */
export const getSiteImageSlots = createServerFn({ method: "GET" }).handler(
  async (): Promise<SiteImageSlotsResolved> => {
    if (slotsCache && Date.now() < slotsCache.expiresAt) return slotsCache.data;

    const fromEdge = await readSharedEdgeCache<SiteImageSlotsResolved>(SLOTS_EDGE_CACHE_KEY);
    if (fromEdge) {
      slotsCache = { data: fromEdge, expiresAt: Date.now() + SLOTS_CACHE_TTL };
      return fromEdge;
    }

    try {
      const { data: slotRows, error: slotsError } = await supabaseAdmin
        .from("site_image_slots")
        .select("slot_key, media_id");
      if (slotsError) throw slotsError;

      const mediaIds = (slotRows ?? [])
        .map((r) => r.media_id)
        .filter((id): id is string => id !== null);

      const mediaById = new Map<string, string | null>();
      if (mediaIds.length > 0) {
        const { data: mediaRows, error: mediaError } = await supabaseAdmin
          .from("blog_media_library")
          .select("id, storage_path")
          .in("id", mediaIds);
        if (mediaError) throw mediaError;
        for (const row of mediaRows ?? []) mediaById.set(row.id, row.storage_path);
      }

      const resolved = emptyResolved();
      for (const row of slotRows ?? []) {
        if (!row.media_id) continue;
        const storagePath = mediaById.get(row.media_id);
        if (!storagePath) continue; // sin asignar, no elegible, o la fila ya no existe

        const slotKey = row.slot_key as SiteImageSlotKey;
        const widths = widthsForSlot(slotKey);
        const urls = widths.map((width) =>
          mediaLibraryProxyUrl(deriveVariantPath(storagePath, width)),
        );
        resolved[slotKey] = {
          src: urls[urls.length - 1],
          srcSet: urls.map((url, i) => `${url} ${widths[i]}w`).join(", "),
        };
      }

      slotsCache = { data: resolved, expiresAt: Date.now() + SLOTS_CACHE_TTL };
      await writeSharedEdgeCache(SLOTS_EDGE_CACHE_KEY, resolved, SLOTS_CACHE_TTL / 1000);
      return resolved;
    } catch (err) {
      console.error("[site-image-slots] getSiteImageSlots fallback a defaults:", err);
      return emptyResolved();
    }
  },
);
